import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Check, X, Sparkle, ChartLine, Infinity as InfinityIcon, Brain, Camera,
  FilePdf, Barbell, Users, ListChecks, Bell, Heartbeat, Trophy,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const inr = (n) => `₹${n.toLocaleString("en-IN")}`;

// Roles that default to the athlete-scoped view
const ATHLETE_ROLES = new Set([
  "athlete", "general_client", "general_fitness", "parent",
]);
const COACH_ROLES = new Set([
  "strength_coach", "fitness_trainer", "sports_coach", "academy_admin",
]);

const ATHLETE_BENEFITS = [
  { slug: "unlimited-programs", icon: Brain,       label: "Unlimited AI-generated training programs" },
  { slug: "recovery-insights",  icon: Heartbeat,   label: "Advanced recovery, readiness & sleep insights" },
  { slug: "pb-charts",          icon: Trophy,      label: "Personal-best tracking with progression charts" },
  { slug: "form-check",         icon: Camera,      label: "AI Form Check — photo & video frame analysis" },
  { slug: "analytics",          icon: ChartLine,   label: "Deep analytics: muscle volume, macros, correlations" },
  { slug: "pdf-reports",        icon: FilePdf,     label: "Branded PDF progress reports you can share" },
  { slug: "priority-model",     icon: InfinityIcon,label: "Priority Claude Sonnet 4.5 access" },
  { slug: "early-access",       icon: Sparkle,     label: "Early access to new athlete features" },
];

const COACH_BENEFITS = [
  { icon: Users,       label: "Manage clients with full progress visibility" },
  { icon: ListChecks,  label: "Program builder with drag-and-drop exercise library" },
  { icon: Barbell,     label: "Full exercise library with cues, mistakes & media" },
  { icon: Bell,        label: "Real-time alerts: readiness, pain, missed sessions" },
  { icon: FilePdf,     label: "Branded PDF assessment & progress reports" },
  { icon: ChartLine,   label: "Client analytics: adherence, PBs, volume trends" },
  { icon: Brain,       label: "AI program generation for every client" },
  { icon: InfinityIcon,label: "Priority Claude Sonnet 4.5 access" },
];

// Free vs Pro comparison for athlete tab
const ATHLETE_COMPARISON = [
  { feature: "AI-generated programs",         free: "3 / month",     pro: "Unlimited" },
  { feature: "Advanced analytics",            free: false,           pro: true },
  { feature: "AI Form Check",                 free: false,           pro: true },
  { feature: "Recovery & readiness insights", free: "Basic",         pro: "Advanced" },
  { feature: "PB tracking & charts",          free: "Last 30 days",  pro: "Full history" },
  { feature: "Branded PDF reports",           free: false,           pro: true },
  { feature: "Priority AI model",             free: false,           pro: "Sonnet 4.5" },
];

function TabPill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-5 py-2 text-xs label-tiny tracking-widest transition-colors border ${
        active
          ? "bg-brand-accent text-surface-base border-brand-accent"
          : "border-white/20 text-text-secondary hover:border-brand-accent hover:text-brand-accent"
      }`}
    >
      {children}
    </button>
  );
}

function ComparisonCell({ value }) {
  if (value === true)  return <Check size={18} weight="bold" className="text-brand-accent inline" />;
  if (value === false) return <X size={16} weight="bold" className="text-text-secondary/60 inline" />;
  return <span className="text-sm tabular-nums">{value}</span>;
}

export default function Upgrade() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, checkAuth } = useAuth();
  const [packages, setPackages] = useState([]);
  const [status, setStatus] = useState(null);
  const [loadingPkg, setLoadingPkg] = useState(null);

  // Determine default tab from user role if not in URL
  const defaultTab = useMemo(() => {
    const role = user?.primary_role;
    if (COACH_ROLES.has(role)) return "coach";
    if (ATHLETE_ROLES.has(role)) return "athlete";
    return "athlete"; // safe default (super_admin etc.)
  }, [user]);

  const tabParam = searchParams.get("tab");
  const activeTab = tabParam === "coach" || tabParam === "athlete" ? tabParam : defaultTab;
  const highlightSlug = searchParams.get("highlight") || "";

  // Deep-link highlight: when a locked Pro sidebar item routes here with
  // ?highlight=<slug>, scroll the matching benefit row into view and pulse it.
  useEffect(() => {
    if (!highlightSlug) return;
    // Wait a tick for the benefits grid to render.
    const t = setTimeout(() => {
      const el = document.getElementById(`benefit-${highlightSlug}`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("upgrade-highlight-pulse");
      const clean = setTimeout(() => el.classList.remove("upgrade-highlight-pulse"), 2400);
      return () => clearTimeout(clean);
    }, 350);
    return () => clearTimeout(t);
  }, [highlightSlug, activeTab, packages]);

  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  const refresh = async () => {
    const [{ data: pkgs }, { data: st }] = await Promise.all([
      api.get("/pro/packages"),
      api.get("/pro/status"),
    ]);
    setPackages(pkgs);
    setStatus(st);
  };
  useEffect(() => { refresh(); }, []);

  const checkout = async (pkgId) => {
    if (typeof window.Razorpay !== "function") {
      toast.error("Payment SDK not loaded — refresh the page");
      return;
    }
    setLoadingPkg(pkgId);
    try {
      const { data: order } = await api.post("/razorpay/orders", { package_id: pkgId });
      const options = {
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: "Edge Training",
        description: order.label,
        order_id: order.order_id,
        prefill: {
          name: order.user_name || user?.name || "",
          email: order.user_email || user?.email || "",
        },
        theme: { color: "#1E3A8A" },
        handler: async (response) => {
          try {
            await api.post("/razorpay/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              package_id: pkgId,
            });
            toast.success("Welcome to Edge Training Pro", { duration: 5000 });
            await checkAuth();
            await refresh();
            navigate(`/upgrade?tab=${activeTab}`, { replace: true });
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed");
          } finally {
            setLoadingPkg(null);
          }
        },
        modal: {
          ondismiss: () => {
            setLoadingPkg(null);
            toast.info("Checkout cancelled");
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed");
        setLoadingPkg(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoadingPkg(null);
    }
  };

  const athletePkgs = packages.filter((p) => !p.id.startsWith("coach"));
  const coachPkgs = packages.filter((p) => p.id.startsWith("coach"));

  const isAthlete = activeTab === "athlete";
  const isCoachActive = status?.is_coach_pro;
  const isProActive = status?.is_pro;

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="upgrade-page">
      {/* Hero */}
      <div className="mb-8 text-center">
        <div className="label-tiny text-brand-accent mb-2">// PROTOCOL UPGRADE</div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl">
          {isAthlete ? (
            <>TRAIN LIKE A <span className="text-heat neon-yellow">PRO.</span></>
          ) : (
            <>SCALE YOUR <span className="text-heat neon-yellow">COACHING.</span></>
          )}
        </h1>
        <p className="text-text-secondary mt-3 max-w-xl mx-auto text-sm">
          {isAthlete
            ? "Unlimited AI programs, deep recovery insights, PB tracking & AI form coaching. INR pricing."
            : "Manage clients, build programs, run your remote coaching business. INR pricing."}
        </p>
      </div>

      {/* Tab pills */}
      <div className="flex justify-center gap-3 mb-10" data-testid="upgrade-tabs">
        <TabPill
          active={isAthlete}
          onClick={() => setTab("athlete")}
          testid="tab-athlete"
        >
          FOR ATHLETES
        </TabPill>
        <TabPill
          active={!isAthlete}
          onClick={() => setTab("coach")}
          testid="tab-coach"
        >
          FOR COACHES
        </TabPill>
      </div>

      {/* Active status banners */}
      {isAthlete && isProActive && (
        <div className="tactical-card active p-6 mb-8 text-center" data-testid="pro-active-banner">
          <Sparkle size={28} weight="fill" className="text-brand-accent mx-auto mb-2" />
          <h2 className="font-heading text-2xl mb-1">YOU&apos;RE EDGE TRAINING PRO</h2>
          <p className="text-sm text-text-secondary">
            Active until {new Date(status.pro_until).toLocaleDateString()}
          </p>
        </div>
      )}
      {!isAthlete && isCoachActive && (
        <div className="tactical-card active p-6 mb-8 text-center" data-testid="coach-active-banner">
          <Sparkle size={28} weight="fill" className="text-brand-accent mx-auto mb-2" />
          <h2 className="font-heading text-2xl mb-1">COACH TIER ACTIVE</h2>
          <p className="text-sm text-text-secondary">
            Active until {status?.coach_pro_until && new Date(status.coach_pro_until).toLocaleDateString()}
          </p>
        </div>
      )}

      {isAthlete && !isProActive && status && (
        <div className="tactical-card p-4 mb-8 flex items-center justify-center gap-3 text-sm" data-testid="free-tier-banner">
          <span className="label-tiny text-text-secondary">FREE TIER USAGE</span>
          <span className="font-heading text-xl tabular-nums text-brand-accent">
            {status.programs_used_this_month} / {status.programs_limit}
          </span>
          <span className="label-tiny text-text-secondary">AI PROGRAMS THIS MONTH</span>
        </div>
      )}

      {/* Package cards */}
      {isAthlete ? (
        <div className="grid md:grid-cols-2 gap-6 mb-10" data-testid="athlete-plans">
          {athletePkgs.map((p) => {
            const isYearly = p.id === "pro_yearly";
            const monthly = isYearly ? Math.round(p.amount_inr / 12) : p.amount_inr;
            return (
              <div
                key={p.id}
                className={`tactical-card p-8 ${isYearly ? "active" : ""}`}
                data-testid={`package-${p.id}`}
              >
                {isYearly && (
                  <div className="label-tiny text-brand-accent mb-3 flex items-center gap-1">
                    <Sparkle size={12} weight="fill" /> BEST VALUE — SAVE 17%
                  </div>
                )}
                <h3 className="font-heading text-2xl mb-1">{p.label.toUpperCase()}</h3>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-heading text-5xl tabular-nums">{inr(p.amount_inr)}</span>
                  <span className="text-text-secondary text-sm">/ {isYearly ? "year" : "month"}</span>
                </div>
                {isYearly ? (
                  <div className="label-tiny text-text-secondary mb-6 tabular-nums">
                    {inr(monthly)}/mo billed annually
                  </div>
                ) : (
                  <div className="label-tiny text-text-secondary mb-6">Cancel anytime</div>
                )}
                <button
                  onClick={() => checkout(p.id)}
                  disabled={loadingPkg === p.id || isProActive}
                  className={`w-full py-3 text-xs label-tiny tracking-widest transition-colors ${
                    isYearly ? "btn-kinetic" : "border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-surface-base"
                  } disabled:opacity-50`}
                  data-testid={`checkout-${p.id}-button`}
                >
                  {loadingPkg === p.id ? "LAUNCHING..." : isProActive ? "ALREADY PRO" : `UPGRADE → ${inr(p.amount_inr)}`}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-6 mb-10" data-testid="coach-plans">
          {coachPkgs.map((p) => {
            const isPlus = p.id.includes("plus");
            const isYearly = p.id.includes("yearly");
            const limit = isPlus ? 40 : 10;
            return (
              <div
                key={p.id}
                className={`tactical-card p-6 ${isPlus ? "active" : ""}`}
                data-testid={`package-${p.id}`}
              >
                {isPlus && (
                  <div className="label-tiny text-brand-accent mb-3 flex items-center gap-1">
                    <Sparkle size={12} weight="fill" /> POWER TIER
                  </div>
                )}
                <h3 className="font-heading text-xl mb-1">{p.label.toUpperCase()}</h3>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-heading text-4xl tabular-nums">{inr(p.amount_inr)}</span>
                  <span className="text-text-secondary text-xs">/ {isYearly ? "yr" : "mo"}</span>
                </div>
                <div className="label-tiny text-text-secondary mb-4">Up to {limit} active clients</div>
                <button
                  onClick={() => checkout(p.id)}
                  disabled={loadingPkg === p.id || isCoachActive}
                  className={`w-full py-3 text-xs label-tiny tracking-widest transition-colors ${
                    isPlus ? "btn-kinetic" : "border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-surface-base"
                  } disabled:opacity-50`}
                  data-testid={`checkout-${p.id}-button`}
                >
                  {loadingPkg === p.id ? "LAUNCHING..." : isCoachActive ? "COACH ACTIVE" : `UPGRADE → ${inr(p.amount_inr)}`}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Free vs Pro comparison (athlete only) */}
      {isAthlete && (
        <div className="tactical-card p-6 sm:p-8 mb-8" data-testid="athlete-comparison">
          <div className="label-tiny text-brand-accent mb-4">// FREE vs PRO</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 pr-4 label-tiny text-text-secondary font-normal">FEATURE</th>
                  <th className="text-center py-3 px-4 label-tiny text-text-secondary font-normal">FREE</th>
                  <th className="text-center py-3 pl-4 label-tiny text-brand-accent font-normal">PRO</th>
                </tr>
              </thead>
              <tbody>
                {ATHLETE_COMPARISON.map((row, i) => (
                  <tr key={i} className="border-b border-white/5" data-testid={`compare-row-${i}`}>
                    <td className="py-3 pr-4">{row.feature}</td>
                    <td className="py-3 px-4 text-center"><ComparisonCell value={row.free} /></td>
                    <td className="py-3 pl-4 text-center"><ComparisonCell value={row.pro} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Benefits grid */}
      <div className="tactical-card p-8" data-testid="benefits-grid">
        <div className="label-tiny text-brand-accent mb-4">// WHAT YOU UNLOCK</div>
        <div className="grid md:grid-cols-2 gap-4">
          {(isAthlete ? ATHLETE_BENEFITS : COACH_BENEFITS).map((f, i) => {
            const slug = f.slug || `feature-${i}`;
            const isHighlighted = slug === highlightSlug;
            const showTrialCta = isHighlighted && !isProActive && athletePkgs.length > 0 && isAthlete;
            const trialPkg = athletePkgs.find((p) => p.id === "pro_monthly") || athletePkgs[0];
            return (
              <div
                key={slug}
                id={`benefit-${slug}`}
                className={`flex items-start gap-3 p-2 -m-2 transition-all rounded-sm ${isHighlighted ? "col-span-1 md:col-span-2" : ""}`}
                data-testid={`pro-feature-${slug}`}
              >
                <div className="w-9 h-9 flex-shrink-0 border border-brand-accent flex items-center justify-center">
                  <f.icon size={18} weight="duotone" className="text-brand-accent" />
                </div>
                <div className="pt-1.5 flex-1 min-w-0">
                  <span className="text-sm">{f.label}</span>
                  {showTrialCta && trialPkg && (
                    <div className="mt-2.5 flex items-center gap-3 flex-wrap" data-testid={`benefit-trial-cta-${slug}`}>
                      <button
                        onClick={() => checkout(trialPkg.id)}
                        disabled={loadingPkg === trialPkg.id}
                        className="btn-kinetic px-4 py-2 text-[11px] label-tiny tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid="benefit-start-trial-button"
                      >
                        {loadingPkg === trialPkg.id ? "STARTING…" : "START PRO TRIAL"}
                        <Sparkle size={11} weight="fill" />
                      </button>
                      <span className="text-[11px] text-text-secondary">
                        {inr(trialPkg.amount_inr)}/month · cancel anytime
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-center text-xs text-text-secondary mt-6">
        Test mode — use any Razorpay test UPI/card/netbanking. Try card <span className="text-white">4111 1111 1111 1111</span>, any future date, any CVC.
      </p>
    </div>
  );
}
